import { Fragment } from 'react';

/**
 * TEXTO LEGAL COM TÍTULOS EM NEGRITO (/termos e /politica)
 *
 * Os dois campos (`termos_uso` e `politica_privacidade`) são TEXTO PURO no CRM — o site
 * sempre os imprimiu crus dentro de um <p white-space: pre-wrap>, então não há como o
 * cliente marcar um título: HTML digitado no campo apareceria escapado na tela.
 *
 * Aqui o texto continua igual no banco; só a RENDERIZAÇÃO reconhece os títulos e os
 * imprime em negrito:
 *   - `1. Termos`, `4. Limitações`…  → linha inteira em negrito (título numerado);
 *   - `Desativar cookies`, `Mais informações`… → linha inteira (título sem número);
 *   - `a. Modificar ou copiar…`      → só o marcador `a.` em negrito, o texto segue normal.
 * Linha de parágrafo (longa, ou terminada em `.`/`;`/`:`/`,`) nunca vira título.
 *
 * ⚠️ GATE POR TENANT, de propósito: o pedido é do cs2409 (Lord Imóveis) e estas duas
 * páginas são as mesmas dos ~300 sites — mudar a aparência do texto legal de todo mundo
 * sem pedido seria regressão visual. Para ligar em outro tenant, some o codsite em
 * SITES_TITULO_NEGRITO.
 */
export const SITES_TITULO_NEGRITO = new Set<number>([2409]);

export function siteUsaTitulosEmNegrito(site: any): boolean {
    return SITES_TITULO_NEGRITO.has(Number(site?.id));
}

type Tipo = 'titulo' | 'alinea' | null;

/** Que papel esta linha tem no texto legal (null = parágrafo comum). */
export function classificaLinhaLegal(linha: string): Tipo {
    const s = String(linha ?? '').trim();
    if (!s) return null;
    const terminaEmPontuacao = /[.;:,]$/.test(s);
    // "1. Termos" — título numerado: curto e sem pontuação de fim de frase.
    if (/^\d+\.\s+\S/.test(s)) return !terminaEmPontuacao && s.length <= 80 ? 'titulo' : null;
    // "a. Modificar ou copiar os materiais;" — alínea: negrito só no marcador.
    if (/^[a-z]\.\s/.test(s)) return 'alinea';
    // "Desativar cookies" — título sem numeração.
    if (!terminaEmPontuacao && s.length <= 60) return 'titulo';
    return null;
}

const LegalText: React.FC<{ texto: string; site: any }> = ({ texto, site }) => {
    const conteudo = String(texto ?? '');
    if (!siteUsaTitulosEmNegrito(site)) return <>{conteudo}</>;

    const linhas = conteudo.split('\n');
    return (
        <>
            {linhas.map((linha, i) => {
                // o \n é reposto entre as linhas: o <p> tem white-space: pre-wrap e é ele
                // quem desenha as quebras — sem isso o texto viraria um bloco só.
                const quebra = i < linhas.length - 1 ? '\n' : '';
                const tipo = classificaLinhaLegal(linha);

                if (tipo === 'titulo') {
                    return (
                        <Fragment key={i}>
                            <strong>{linha}</strong>
                            {quebra}
                        </Fragment>
                    );
                }

                if (tipo === 'alinea') {
                    const m = linha.match(/^(\s*[a-z]\.)([\s\S]*)$/);
                    if (m) {
                        return (
                            <Fragment key={i}>
                                <strong>{m[1]}</strong>
                                {m[2]}
                                {quebra}
                            </Fragment>
                        );
                    }
                }

                return (
                    <Fragment key={i}>
                        {linha}
                        {quebra}
                    </Fragment>
                );
            })}
        </>
    );
};

export default LegalText;
